import i18n from '@/settings/i18n';

const t = i18n.init('errors');

export const formatStatusCodeError = (input?: string) => {
  const backupString =
    'There was an error processing your request. Please try again.';

  if (!input) {
    return backupString;
  }

  if (/\b\d{3}\b/.test(input)) {
    return backupString;
  }

  return input;
};

export const isCheckoutAlreadyPaidError = (errorMessage?: string): boolean => {
  if (!errorMessage) {
    return false;
  }

  const message = errorMessage.toLowerCase();

  return message.includes('checkout has already been paid');
};

export const areCheckoutLinesOutOfStock = (errorMessage?: string): boolean => {
  if (!errorMessage) {
    return false;
  }

  return errorMessage
    .toLowerCase()
    .includes('payment cannot be processed due to out of stock items');
};

// Backend/API error messages known to be shown to customers.
const KNOWN_BACKEND_ERRORS = [
  'There was an error processing your request. Please try again.',
  'There was an error processing your payment. Please try again.',
  // Fallback thrown by the shared CheckoutApi client (@lambda/apis) when the
  // checkout service response has no message. Typo is in the source string.
  'An error has occured. Please try again later.',
  // Checkout service (MsCheckout)
  'Checkout has already been paid',
  'Checkout is already processing',
  'Payment cannot be processed due to out of stock items',
  'Checkout is missing billing address',
  'Checkout is missing shipping address',
  'Invalid payment processor',
  'Invalid shipping address',
  'Error calculating taxes and duties',
  'Checkout not found',
  'Cart not found',
  'Order not found',
  'Customer not found',
  // Stripe PaymentIntent errors surfaced via the thank-you page redirect
  'Your card was declined.',
  'Your card has insufficient funds.',
  'Your card has expired.',
  "Your card's security code is incorrect.",
  'Your card number is incorrect.',
  'A processing error occurred.',
  // AWS Cognito (identity) errors surfaced in the auth forms
  'Incorrect username or password.',
  'Invalid verification code provided, please try again.',
  'Attempt limit exceeded, please try again after some time.',
  'Password attempts exceeded',
  'User does not exist.',
  'User already exists',
  'An account with the given email already exists.',
  'Invalid session for the user, session is expired.',
];

const KNOWN_BACKEND_ERRORS_BY_KEY = new Map(
  KNOWN_BACKEND_ERRORS.map((message) => [
    message.toLowerCase().trim(),
    message,
  ]),
);

/**
 * Translates a backend error message before showing it to the customer.
 *
 * - Messages in the known catalog are returned in the current locale.
 * - Messages containing an HTTP status code (e.g. axios' "Request failed
 *   with status code 400") are replaced by the fallback.
 * - Unknown messages are shown as-is on English stores only; on other
 *   locales the translated fallback is used so raw English never leaks.
 *
 * Not for messages that are already localized at the source (e.g. Stripe
 * Elements client errors, which follow the Elements `locale`) — those
 * should keep using `formatStatusCodeError`.
 */
export const translateBackendError = (
  rawInput?: string | string[] | null,
  fallback?: string,
): string => {
  const genericError =
    fallback ??
    t`There was an error processing your request. Please try again.`;

  // NestJS services return `message` as an array for validation errors and
  // for CheckoutError responses (`{ message: [string] }`).
  const input = Array.isArray(rawInput) ? rawInput.join(' ') : rawInput;

  if (!input || typeof input !== 'string') {
    return genericError;
  }

  // Known messages are matched before the status-code scrub so an embedded
  // number (e.g. an order id) doesn't hide a translatable message.
  // The payments service wraps these two phrases in longer messages, so they
  // are matched by substring (mirroring isCheckoutAlreadyPaidError and
  // areCheckoutLinesOutOfStock) rather than by exact catalog lookup.
  if (isCheckoutAlreadyPaidError(input)) {
    return t`Checkout has already been paid`;
  }

  if (areCheckoutLinesOutOfStock(input)) {
    return t`Payment cannot be processed due to out of stock items`;
  }

  const canonical = KNOWN_BACKEND_ERRORS_BY_KEY.get(input.toLowerCase().trim());

  if (canonical) {
    return t(canonical);
  }

  if (/\b\d{3}\b/.test(input)) {
    return genericError;
  }

  return i18n.getLocale().startsWith('en') ? input : genericError;
};
